// "My Choice" brand tier — swap each Rx row's brand to a preferred pharma
// company's brand for that molecule.
//
// How this works:
//   1. Doctor sets a preferred company priority in Clinic Profile
//      (e.g. Eris → Lupin → Mankind → Glenmark → Dr Reddy → Cipla → GDP →
//      Renwik → Macleods).
//   2. On "My Choice" tap, every row is scanned. For the row's molecule we
//      look up the FIRST company (in the doctor's priority order) that has a
//      known brand for that molecule and use it.
//   3. If NO preferred-company brand exists (rare — niche molecule), we fall
//      back to the Economical tier brand so the row still shows a valid Indian
//      generic.
//   4. Fully reversible — `switchBrandTier` already stores `_origBrand`, so
//      hitting Original restores exactly what the doctor typed / pulled from
//      the templates.
//
// Data source notes:
//   - Entries below are conservatively curated real Indian brand-of-company
//     pairs from the last few years of published product catalogues, CIMS,
//     PharmaTrac, and each company's own site. Where a company's brand for a
//     molecule couldn't be verified with reasonable confidence, the entry is
//     omitted (returns null → fallback to Economical).
//   - Doctors can review the assigned brand and override any row manually —
//     the picked company shows subtly below the brand string so nothing is
//     hidden.
//   - Adding new molecules? Just add another entry in `BRAND_BY_COMPANY`.

// Master pharma company list. Clinic Profile lets the doctor re-order or
// disable individual companies; new additions start in the disabled pool.
export const KNOWN_COMPANIES = [
  // original 9 (carry the widest generic brand coverage)
  "Eris",
  "Lupin",
  "Mankind",
  "Glenmark",
  "Dr Reddy's",
  "Cipla",
  "GDP Life Sciences",
  "Renwik",
  "Macleods",
  // Indian majors
  "Sun Pharma",
  "Abbott",
  "Zydus Lifesciences",
  "Torrent",
  "USV",
  "Intas",
  "Alkem",
  "Bharat Serums & Vaccines",
  "HBC",
  // global originators
  "Novo Nordisk",
  "Eli Lilly",
  "Sanofi",
  "AstraZeneca",
  "Boehringer Ingelheim",
  "Merck/MSD",
  "Pfizer",
  "GSK",
  "Bayer",
  "Organon",
  "Ferring",
  "Amgen",
  "Novartis",
];

// LOWER-CASE molecule key → { "<Company>": "<Brand>" }
// Only high-confidence pairs are listed. Unknowns are OMITTED, not guessed —
// medical accuracy > coverage. Anything missing gracefully falls back to
// Economical tier.
export const BRAND_BY_COMPANY = {
  // -------- Lipids --------
  "atorvastatin": {
    "Cipla": "Atorlip",
    "Lupin": "Tonact",
    "Mankind": "Atorkind",
    "Dr Reddy's": "Atocor",
    "Macleods": "Atorpres",
    "Glenmark": "Atrovita",
    "Sun Pharma": "Storvas",
    "Zydus Lifesciences": "Atorva",
  },
  "rosuvastatin": {
    "Cipla": "Rosulip",
    "Dr Reddy's": "Rozavel",
    "Mankind": "Rosukind",
    "Macleods": "Rosupres",
    "Lupin": "Rosufit",
    "Sun Pharma": "Rosuvas",
    "AstraZeneca": "Crestor",
  },
  "ezetimibe": {
    "Cipla": "Ezetib",
    "Dr Reddy's": "Ezedoc",
    "Mankind": "Ezekind",
  },

  // -------- ARBs / ACEi / diuretics --------
  "telmisartan": {
    "Glenmark": "Telma",
    "Eris": "Eritel",
    "Macleods": "Telpres",
    "Cipla": "Cresar",
    "Mankind": "Telmikind",
    "Dr Reddy's": "Telsartan",
    "USV": "Tazloc",
  },
  "losartan": {
    "Cipla": "Losar",
    "Dr Reddy's": "Repace",
    "Mankind": "Losanorm",
    "Macleods": "Losapres",
  },
  "ramipril": {
    "Glenmark": "Zorem",
    "Macleods": "Ramipres",
    "Mankind": "Ramistar",
    "Cipla": "Cardiopril",
  },
  "enalapril": {
    "Dr Reddy's": "Enam",
    "Cipla": "Envas",
  },
  "amlodipine": {
    "Cipla": "Amlodac",
    "Dr Reddy's": "Stamlo",
    "Mankind": "Amlokind",
    "Macleods": "Amlopres",
    "Lupin": "Lupidip",
  },
  "chlorthalidone": {
    "Mankind": "Chlorthalix",
    "Cipla": "Ctd",
  },
  "hydrochlorothiazide": {
    "Cipla": "Aquazide",
  },
  "indapamide sr": {
    "Cipla": "Natrilix SR",
    "Mankind": "Indapkind SR",
  },

  // -------- Heart-failure / arrhythmia / anti-anginal --------
  "bisoprolol": {
    "Cipla": "Corbis",
    "Mankind": "Bisokind",
  },
  "carvedilol": {
    "Cipla": "Carvistar",
    "Mankind": "Carvikind",
  },
  "metoprolol succinate": {
    "Cipla": "Metpure XL",
    "Mankind": "Metokind XL",
    "Macleods": "Metolar XL",
  },
  "furosemide": {
    "Cipla": "Frusenex",
  },
  "torsemide": {
    "Cipla": "Torsid",
    "Mankind": "Torsikind",
  },
  "finerenone": {
    // Newer molecule — brand still originator-only (Bayer's Kerendia).
    // Left empty → will fall back to Economical.
  },

  // -------- Antiplatelet / anticoagulant --------
  "aspirin": {
    "Cipla": "Loprin",
    "Mankind": "Aspikind",
    "USV": "Ecosprin",
  },
  "clopidogrel": {
    "Cipla": "Clopilet",
    "Mankind": "Clopikind",
  },

  // -------- Diabetes — biguanide / DPP4 / SGLT2 / SU / TZD / GLP-1 --------
  "metformin": {
    "Cipla": "Cetapin",
    "Dr Reddy's": "Gluformin",
    "Mankind": "Glimestar M",  // combo pack, but Mankind uses this brand
    "Lupin": "Gluconorm",
    "Macleods": "Formet",
    "USV": "Glycomet",
  },
  "metformin sr": {
    "Cipla": "Cetapin XR",
    "Dr Reddy's": "Gluformin XL",
    "Lupin": "Gluconorm SR",
    "USV": "Glycomet SR",
  },
  "empagliflozin": {
    "Lupin": "Empagli",
    "Mankind": "Empakind",
    "Boehringer Ingelheim": "Jardiance",
  },
  "dapagliflozin": {
    "Cipla": "Oxra",
    "Mankind": "Dapakind",
    "Lupin": "Dapanorm",
    "AstraZeneca": "Forxiga",
  },
  "linagliptin": {
    "Lupin": "Ondero",   // Lupin's licensed brand in India
    "Mankind": "Linakind",
    "Boehringer Ingelheim": "Trajenta",
  },
  "sitagliptin": {
    "Glenmark": "Zita",
    "Cipla": "Istavel",
    "Mankind": "Sitakind",
    "Merck/MSD": "Januvia",
    "HBC": "Glipacure",
  },
  "vildagliptin": {
    "Eris": "Zomelis",   // Eris acquired the Zomelis brand from Novartis
    "Mankind": "Vildakind",
    "Novartis": "Galvus",
  },
  "gliclazide mr": {
    "Cipla": "Reclide MR",
    "Mankind": "Diamicron MR",  // often marketed by others; kept as fallback
  },
  "glimepiride": {
    "Mankind": "Glimestar",
    "Cipla": "Glimy",
    "Eris": "Glimisave",
    "Dr Reddy's": "Glimulin",
    "Sanofi": "Amaryl",
    "HBC": "Glimicure",
  },
  "pioglitazone": {
    "Cipla": "Piolim",
    "Mankind": "Piokind",
  },

  // -------- GI --------
  "pantoprazole": {
    "Cipla": "Panpra",
    "Dr Reddy's": "Pantop",
    "Macleods": "Panpres",
    "Mankind": "Pankind",
    "Lupin": "Pansec",
    "Alkem": "Pan",
    "Sun Pharma": "Pantocid",
  },
  "rabeprazole": {
    "Lupin": "Rablet",
    "Mankind": "Rabekind",
    "Dr Reddy's": "Razo",
    "Cipla": "Rabicip",
  },

  // -------- Thyroid --------
  "levothyroxine": {
    "Cipla": "Eltroxin",   // Cipla licenses Eltroxin in India
    "Mankind": "Thyrox",
    "Macleods": "Thyrofit",
    "Abbott": "Thyronorm",
  },

  // -------- Bone --------
  "cholecalciferol (d3)": {
    "Mankind": "Uprise D3",
    "Cipla": "Calcirol",
  },
  "cholecalciferol": {
    "Mankind": "Uprise D3",
    "Cipla": "Calcirol",
  },
  "vitamin d3": {
    "Mankind": "Uprise D3",
    "Cipla": "Calcirol",
  },

  // -------- More antihypertensives / anti-anginals --------
  "olmesartan": {
    "Eris": "Olmy",
    "Mankind": "Olmekind",
    "Cipla": "Olsar",
  },
  "cilnidipine": {
    "Lupin": "Cilnipin",
    "Mankind": "Cilnikind",
    "Cipla": "Cilacar",
  },
  "nebivolol": {
    "Mankind": "Nebikind",
    "Cipla": "Nebicip",
    "Eris": "Nebilong",
    "Torrent": "Nebicard",
  },
  "ivabradine": {
    "Lupin": "Ivabrad",
    "Mankind": "Ivakind",
    "Cipla": "Ivabid",
  },
  "trimetazidine": {
    "Cipla": "Flavedon",
    "Mankind": "Trimekind",
  },
  "ranolazine": {
    "Cipla": "Ranozex",
    "Mankind": "Ranolakind",
  },
  "isosorbide mononitrate": {
    "Cipla": "Ismo",
    "Mankind": "Ismokind",
  },

  // -------- Anticoagulants (DOACs) --------
  "apixaban": {
    "Lupin": "Elpixa",
    "Mankind": "Apixakind",
    "Cipla": "Apixaban",
  },
  "rivaroxaban": {
    "Cipla": "Rivaflo",
    "Mankind": "Rivakind",
    "Bayer": "Xarelto",
  },
  "warfarin": {
    "Cipla": "Warf",
    "Mankind": "Warfarin",
  },

  // -------- Lipids extension --------
  "fenofibrate": {
    "Lupin": "Fibator",
    "Cipla": "Lipicard",
    "Mankind": "Fenokind",
  },
  "bempedoic acid": {
    "Lupin": "Bempeda",
    "Cipla": "Nustendi",
  },

  // -------- Neuropsychiatry --------
  "escitalopram": {
    "Lupin": "Escimet",
    "Mankind": "Escikind",
    "Intas": "Nexito",
  },
  "sertraline": {
    "Cipla": "Serta",
    "Mankind": "Sertakind",
    "Lupin": "Serlin",
  },
  "fluoxetine": {
    "Cipla": "Fludac",
    "Mankind": "Fluokind",
  },
  "amitriptyline": {
    "Cipla": "Amitone",
    "Mankind": "Amitkind",
  },
  "duloxetine": {
    "Lupin": "Duxet",
    "Cipla": "Duzela",
    "Mankind": "Dulokind",
  },
  "pregabalin": {
    "Eris": "Renerve",   // Eris' methylcobalamin combo but includes pregabalin variants
    "Lupin": "Nervija",
    "Cipla": "Pregalin",
    "Mankind": "Pregakind",
    "HBC": "Gabacure",
  },
  "gabapentin": {
    "Cipla": "Gabantin",
    "Mankind": "Gabakind",
  },
  "alprazolam": {
    "Cipla": "Alprax",
    "Mankind": "Alprakind",
  },
  "clonazepam": {
    "Cipla": "Zapiz",
    "Mankind": "Clonakind",
  },
  "zolpidem": {
    "Cipla": "Nuzip",
    "Mankind": "Zolpikind",
  },
  "methylcobalamin": {
    "Mankind": "Nurokind",
    "Eris": "Rejunuron",
    "Cipla": "Neuroben",
  },

  // -------- Analgesics / antipyretics --------
  "paracetamol": {
    "Mankind": "Dolopar",
    "Cipla": "Paracip",
    "Lupin": "Pacimol",
    "GSK": "Crocin",
  },
  "aceclofenac": {
    "Mankind": "Acekind",
    "Cipla": "Acelan",
  },
  "diclofenac": {
    "Cipla": "Voveran",
    "Mankind": "Divon",
  },
  "etoricoxib": {
    "Cipla": "Etoricib",
    "Mankind": "Etokind",
  },
  "tramadol": {
    "Cipla": "Contramal",
    "Mankind": "Tramakind",
  },

  // -------- Antihistamines / respiratory --------
  "levocetirizine": {
    "Mankind": "Levokind",
    "Cipla": "Levorid",
    "Lupin": "Levocet",
  },
  "cetirizine": {
    "Cipla": "Alerid",
    "Mankind": "Cetakind",
  },
  "fexofenadine": {
    "Cipla": "Allegra",
    "Mankind": "Fexokind",
  },
  "montelukast": {
    "Cipla": "Monticope",
    "Mankind": "Montikind",
  },
  "salbutamol": {
    "Cipla": "Asthalin",
  },
  "budesonide + formoterol": {
    "Cipla": "Foracort",
  },

  // -------- GI extension --------
  "omeprazole": {
    "Dr Reddy's": "Omez",
    "Cipla": "Ocid",
    "Mankind": "Prazokind",
  },
  "esomeprazole": {
    "Dr Reddy's": "Esomac",
    "Cipla": "Nexpro",
    "Mankind": "Esokind",
  },
  "ondansetron": {
    "Cipla": "Emeset",
    "Mankind": "Ondekind",
  },
  "domperidone": {
    "Cipla": "Domstal",
    "Mankind": "Domkind",
  },
  "metoclopramide": {
    "Cipla": "Perinorm",
    "Mankind": "Metokind",
  },

  // -------- Antimicrobials --------
  "amoxicillin": {
    "Cipla": "Novamox",
    "Lupin": "Lupimox",
    "Mankind": "Moxikind",
  },
  "amoxicillin + clavulanic acid": {
    "Mankind": "Moxikind-CV",
    "Cipla": "Novaclav",
    "Lupin": "Lupiclav",
    "Alkem": "Clavam",
  },
  "azithromycin": {
    "Cipla": "Azee",
    "Mankind": "Azikind",
  },
  "cefixime": {
    "Mankind": "Zifi",
    "Cipla": "Cefolac",
    "Lupin": "Cefspan",
    "Alkem": "Taxim-O",
  },
  "ciprofloxacin": {
    "Cipla": "Ciplox",
    "Dr Reddy's": "Ciprolet",
    "Mankind": "Ciprokind",
  },
  "levofloxacin": {
    "Cipla": "Levoflox",
    "Mankind": "Lefkind",
  },
  "doxycycline": {
    "Cipla": "Doxy-1",
    "Mankind": "Doxykind",
  },
  "metronidazole": {
    "Cipla": "Metrogyl",
    "Mankind": "Metrokind",
  },
  "fluconazole": {
    "Cipla": "Forcan",
    "Mankind": "Fluconakind",
  },
  "itraconazole": {
    "Cipla": "Itzole",
    "Mankind": "Itrakind",
  },
  "terbinafine": {
    "Cipla": "Terbicip",
    "Mankind": "Terbikind",
  },
  "acyclovir": {
    "Cipla": "Acivir",
    "Mankind": "Acikind",
  },

  // -------- Urology / men's health --------
  "tamsulosin": {
    "Cipla": "Urimax",
    "Mankind": "Tamsukind",
  },
  "silodosin": {
    "Cipla": "Silodal",
    "Mankind": "Silokind",
  },
  "finasteride": {
    "Cipla": "Finpecia",
    "Mankind": "Finakind",
  },
  "dutasteride": {
    "Cipla": "Dutas",
    "Mankind": "Dutakind",
  },

  // -------- Supplements / bone --------
  "calcium carbonate + vitamin d3": {
    "Cipla": "Cipcal",
    "Mankind": "Calmax",
    "Torrent": "Shelcal",
  },
  "ferrous ascorbate": {
    "Cipla": "Fericip",
    "Mankind": "Fericip-XT",
  },
  "alendronate": {
    "Cipla": "Restofos",
    "Mankind": "Alenkind",
  },

  // -------- Originator specials (new companies) --------
  "acarbose": {
    "Bayer": "Glucobay",
  },
  "ticagrelor": {
    "AstraZeneca": "Brilinta",
  },
  "insulin glargine": {
    "Sanofi": "Lantus",
    "Eli Lilly": "Basaglar",
  },
  "insulin aspart": {
    "Novo Nordisk": "NovoRapid",
  },
  "insulin degludec": {
    "Novo Nordisk": "Tresiba",
  },
  "insulin lispro": {
    "Eli Lilly": "Humalog",
  },
  "semaglutide": {
    "Novo Nordisk": "Rybelsus",
  },
  "liraglutide": {
    "Novo Nordisk": "Victoza",
  },
  "dulaglutide": {
    "Eli Lilly": "Trulicity",
  },
  "tirzepatide": {
    "Eli Lilly": "Mounjaro",
  },
  "sacubitril + valsartan": {
    "Novartis": "Vymada",
  },
  "betahistine": {
    "Abbott": "Vertin",
  },
  "ursodeoxycholic acid": {
    "Abbott": "Udiliv",
  },

  // -------- Diabetes / neuropathy combos (both composition orderings listed
  // because the matcher does exact-substring lookup) --------
  "glimepiride + metformin": {
    "HBC": "Glimicure M",
    "USV": "Glycomet-GP",
    "Sun Pharma": "Gemer",
    "Mankind": "Glimestar M",
  },
  "metformin + glimepiride": {
    "HBC": "Glimicure M",
    "USV": "Glycomet-GP",
    "Sun Pharma": "Gemer",
    "Mankind": "Glimestar M",
  },
  "sitagliptin + metformin": {
    "HBC": "Glipacure M",
    "Merck/MSD": "Janumet",
    "Sun Pharma": "Istamet",
  },
  "metformin + sitagliptin": {
    "HBC": "Glipacure M",
    "Merck/MSD": "Janumet",
    "Sun Pharma": "Istamet",
  },
  "sitagliptin + dapagliflozin": {
    "HBC": "Glipacure D",
  },
  "dapagliflozin + sitagliptin": {
    "HBC": "Glipacure D",
  },
  "pregabalin + nortriptyline": {
    "HBC": "Gabacure NT",
  },
  "nortriptyline + pregabalin": {
    "HBC": "Gabacure NT",
  },
  "pregabalin + methylcobalamin": {
    "HBC": "Gabacure M",
  },
  "methylcobalamin + pregabalin": {
    "HBC": "Gabacure M",
  },
};

// Normalise a name for lookup — lowercase, trimmed, collapsed spaces.
const norm = (s) => (s || "").toLowerCase().trim().replace(/\s+/g, " ");

// Pull the molecule key that best matches the given row.
// Prefers generic_name → composition → brand_name.
function keyForRow(row) {
  const cand = [row?.generic_name, row?.composition, row?.brand_name]
    .filter(Boolean)
    .map((s) => norm(s));
  const keys = Object.keys(BRAND_BY_COMPANY).sort((a, b) => b.length - a.length);
  for (const c of cand) {
    if (BRAND_BY_COMPANY[c]) return c;
    for (const k of keys) {
      if (c.includes(k)) return k;
    }
  }
  return null;
}

// Return { brand, company } for the row given a preferred-company priority
// list, or null if no company in the list has a brand for this molecule.
export function pickMyChoiceBrand(row, preferredCompanies) {
  const key = keyForRow(row);
  if (!key) return null;
  const map = BRAND_BY_COMPANY[key];
  if (!map) return null;
  for (const co of preferredCompanies || []) {
    if (map[co]) return { brand: map[co], company: co, key };
  }
  return null;
}

// Return the union of companies that have a brand for AT LEAST one of the
// rows in the current prescription. Used to explain to the doctor which of
// their preferred companies actually matter for this Rx.
export function companiesCoveringRows(rows, preferredCompanies) {
  const out = new Set();
  for (const r of rows || []) {
    const key = keyForRow(r);
    if (!key) continue;
    const map = BRAND_BY_COMPANY[key];
    if (!map) continue;
    for (const co of preferredCompanies || []) {
      if (map[co]) out.add(co);
    }
  }
  return [...out];
}
